import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TurnstileTokenService {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();
  private widgetId: string | null = null;
  private readonly resetRequest = signal(0);
  /** Increments when Change Email needs a new token after the form is visible. */
  readonly resetRequested = this.resetRequest.asReadonly();

  constructor() { }

  setWidgetId(id: string | null) {
    this.widgetId = id;
  }

  updateToken(token: string | null) {
    this.tokenSubject.next(token);
  }

  getTurnstileToken(): string | null {
    return this.tokenSubject.getValue();
  }

  /** Drop a token that has already been sent to the API. */
  clear() {
    this.tokenSubject.next(null);
  }

  /** Schedule a reset for after the member form is visible. */
  requestReset(): void {
    this.resetRequest.update(count => count + 1);
  }

  /** Clear the stored token and reset the rendered widget so a new challenge can run. */
  reset(widgetId?: string) {
    this.clear();
    const id = widgetId ?? this.widgetId;
    const turnstile = (window as any).turnstile;
    if (id) {
      turnstile?.reset?.(id);
    } else {
      turnstile?.reset?.();
    }
  }

  /**
   * Wait for a valid Turnstile token (used when token needs refresh)
   * @param timeoutMs Maximum time to wait for token (default 10 seconds)
   * @returns Promise that resolves with the token
   */
  waitForToken(timeoutMs: number = 10000): Promise<string> {
    return new Promise((resolve, reject) => {
      const currentToken = this.getTurnstileToken();
      if (currentToken) {
        resolve(currentToken);
        return;
      }

      const subscription = this.token$.subscribe(token => {
        if (token) {
          subscription.unsubscribe();
          resolve(token);
        }
      });

      setTimeout(() => {
        subscription.unsubscribe();
        reject(new Error('Turnstile token timeout'));
      }, timeoutMs);
    });
  }

  /**
   * Ensure we have a valid token, refresh if needed
   * @param widgetId Optional widget ID to reset
   * @returns Promise that resolves with a valid token
   */
  async ensureValidToken(widgetId?: string): Promise<string> {
    const token = this.getTurnstileToken();

    if (token) {
      return token;
    }

    this.reset(widgetId);
    return this.waitForToken();
  }
}
